import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, Search, ShieldCheck, Loader2, RefreshCw, Plus } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("hu-HU", { timeZone: "Europe/Budapest", hour12: false }) : "—");
const regionNames = new Intl.DisplayNames(["hu"], { type: "region" });
function Country({ code }) {
  if (!code) return <span className="text-zinc-600">—</span>;
  let name = code;
  try { name = regionNames.of(code); } catch { /* unknown code */ }
  return <span className="whitespace-nowrap"><span className="mr-1.5 rounded bg-zinc-800 px-1 font-mono text-[10px] text-zinc-300">{code}</span>{name}</span>;
}
const DURS = [["1d", "1 nap"], ["7d", "7 nap"], ["30d", "30 nap"], ["permanent", "Végleges"], ["custom", "Egyedi dátum"]];
const chip = "rounded-md border px-3 py-1.5 text-[12px] transition-colors";

function BanDialog({ ip, onClose, onDone }) {
  const [f, setF] = useState({ ip: ip || "", mode: "silent", reason: "", duration: "7d", expires_at: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setF((x) => ({ ...x, ip: ip || "" })); }, [ip]);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...f, expires_at: f.duration === "custom" && f.expires_at ? new Date(f.expires_at).toISOString() : null };
      await api.post("/admin/bans", body);
      toast.success(`${f.ip} kitiltva`);
      onDone();
    } catch (e) {
      toast.error(apiErr(e?.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={ip !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-zinc-800 bg-[#111113] text-zinc-100" data-testid="ban-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Ban size={17} className="text-red-400" /> IP kitiltása</DialogTitle>
          <DialogDescription className="text-zinc-500">A kitiltott IP-ről az oldal semmilyen része nem érhető el.</DialogDescription>
        </DialogHeader>
        <Input data-testid="ban-ip-input" value={f.ip} onChange={(e) => setF({ ...f, ip: e.target.value })} placeholder="pl. 203.0.113.7" className="h-9 border-zinc-800 bg-zinc-950 font-mono text-[13px]" />
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-wider text-zinc-500">Mit lásson a látogató?</div>
          <div className="grid gap-2 sm:grid-cols-2">
            {[["silent", "Csendes", "„500 Internal Server Error” oldal"], ["informed", "Tájékoztatott", "Kitiltás, indok, lejárat, AutoModDetection"]].map(([k, l, d]) => (
              <button key={k} data-testid={`ban-mode-${k}`} onClick={() => setF({ ...f, mode: k })}
                className={`rounded-lg border p-3 text-left ${f.mode === k ? "border-red-500/60 bg-red-500/10" : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"}`}>
                <div className="text-[13px] font-medium">{l}</div><div className="text-[11px] text-zinc-500">{d}</div>
              </button>
            ))}
          </div>
        </div>
        {f.mode === "informed" && (
          <Textarea data-testid="ban-reason-input" value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} placeholder="Indok (a látogató látja)" className="min-h-[80px] border-zinc-800 bg-zinc-950 text-[13px]" />
        )}
        <div>
          <div className="mb-1.5 text-[10px] uppercase tracking-wider text-zinc-500">Időtartam</div>
          <div className="flex flex-wrap gap-2">
            {DURS.map(([k, l]) => (
              <button key={k} data-testid={`ban-duration-${k}`} onClick={() => setF({ ...f, duration: k })}
                className={`${chip} ${f.duration === k ? "border-red-500/60 bg-red-500/10 text-red-200" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
            ))}
          </div>
          {f.duration === "custom" && (
            <input data-testid="ban-custom-date" type="datetime-local" value={f.expires_at} onChange={(e) => setF({ ...f, expires_at: e.target.value })}
              className="mt-2 h-9 rounded-md border border-zinc-800 bg-zinc-950 px-2 text-[13px] text-zinc-100 [color-scheme:dark]" />
          )}
        </div>
        <DialogFooter className="gap-2">
          <button data-testid="ban-cancel" onClick={onClose} className="rounded-lg border border-zinc-700 px-4 py-2 text-[13px] text-zinc-300">Mégse</button>
          <button data-testid="ban-submit" onClick={submit} disabled={busy || !f.ip.trim() || (f.duration === "custom" && !f.expires_at)}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-[13px] font-semibold text-white hover:bg-red-500 disabled:opacity-40">
            {busy && <Loader2 size={14} className="animate-spin" />} Kitiltás
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ModIpBans() {
  const [data, setData] = useState({ ips: [], my_ip: "", ban_countries: {} });
  const [bans, setBans] = useState([]);
  const [q, setQ] = useState("");
  const [banIp, setBanIp] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([api.get("/admin/ips", { params: q ? { q } : {} }), api.get("/admin/bans")]);
      setData(a.data);
      setBans(b.data.filter((x) => x.active));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const unban = async (id) => {
    await api.delete(`/admin/bans/${id}`);
    toast.success("Tiltás feloldva");
    load();
  };

  return (
    <div className="mx-auto max-w-6xl space-y-5" data-testid="mod-ip-bans">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-2.5 text-zinc-500" />
          <Input data-testid="ip-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="IP keresése…" className="h-9 w-56 border-zinc-800 bg-zinc-950 pl-8 font-mono text-[13px]" />
        </div>
        <button data-testid="ip-refresh" onClick={load} className="grid h-9 w-9 place-items-center rounded-md border border-zinc-800 text-zinc-400 hover:text-zinc-100">{loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}</button>
        <span className="text-[12px] text-zinc-500">Saját IP: <span className="font-mono text-zinc-300" data-testid="my-ip">{data.my_ip}</span></span>
        <button data-testid="ban-manual-btn" onClick={() => setBanIp("")} className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-[12px] font-semibold text-white hover:bg-red-500"><Plus size={14} /> IP kitiltása kézzel</button>
      </div>

      <section className="rounded-xl border border-zinc-800/70 bg-[#111113]">
        <h3 className="border-b border-zinc-800/70 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-wider text-zinc-400">Aktív tiltások ({bans.length})</h3>
        {bans.length === 0 ? <p className="px-4 py-4 text-[13px] text-zinc-600">Nincs aktív tiltás.</p> : bans.map((b) => (
          <div key={b.id} data-testid={`ban-row-${b.ip}`} className="flex flex-wrap items-center gap-3 border-b border-zinc-800/50 px-4 py-2.5 text-[13px] last:border-0">
            <span className="font-mono text-red-300">{b.ip}</span>
            <span className="text-zinc-400" data-testid={`ban-country-${b.ip}`}><Country code={data.ban_countries?.[b.ip] || data.ips.find((r) => r.ip === b.ip)?.country} /></span>
            <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${b.mode === "informed" ? "bg-amber-500/15 text-amber-300" : "bg-zinc-800 text-zinc-400"}`}>{b.mode === "informed" ? "Tájékoztatott" : "Csendes"}</span>
            <span className="text-zinc-500">Lejár: <span className="text-zinc-300">{b.expires_at ? fmt(b.expires_at) : "végleges"}</span></span>
            {b.reason && <span className="max-w-xs truncate text-zinc-500" title={b.reason}>„{b.reason}”</span>}
            <span className="text-[11px] text-zinc-600">{fmt(b.created_at)} · {b.created_by}</span>
            <button data-testid={`unban-${b.ip}`} onClick={() => unban(b.id)} className="ml-auto inline-flex items-center gap-1 rounded-md border border-emerald-600/40 px-2.5 py-1 text-[12px] text-emerald-300 hover:bg-emerald-500/10"><ShieldCheck size={13} /> Feloldás</button>
          </div>
        ))}
      </section>

      <section className="overflow-x-auto rounded-xl border border-zinc-800/70 bg-[#111113]">
        <table className="w-full min-w-[760px] text-left text-[12px]" data-testid="ip-table">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
            <tr className="border-b border-zinc-800/70">{["IP", "Ország", "Kérések", "Utoljára", "Utolsó útvonal", "Böngésző", ""].map((h) => <th key={h} className="px-4 py-2.5 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {data.ips.map((r) => (
              <tr key={r.ip} data-testid={`ip-row-${r.ip}`} className="border-b border-zinc-800/40 last:border-0 hover:bg-zinc-900/40">
                <td className="px-4 py-2 font-mono text-zinc-200">{r.ip}{r.ip === data.my_ip && <span className="ml-1.5 rounded bg-blue-500/15 px-1 text-[10px] text-blue-300">te</span>}</td>
                <td className="px-4 py-2 text-zinc-400" data-testid={`ip-country-${r.ip}`}><Country code={r.country} /></td>
                <td className="px-4 py-2 text-zinc-400">{r.count}</td>
                <td className="px-4 py-2 text-zinc-400">{fmt(r.last_seen)}</td>
                <td className="max-w-[200px] truncate px-4 py-2 font-mono text-zinc-500">{r.last_path}</td>
                <td className="max-w-[220px] truncate px-4 py-2 text-zinc-600" title={r.ua}>{r.ua}</td>
                <td className="px-4 py-2 text-right">
                  {r.ban ? <span className="text-[11px] font-semibold text-red-400">KITILTVA</span> : r.ip !== data.my_ip && (
                    <button data-testid={`ban-btn-${r.ip}`} onClick={() => setBanIp(r.ip)} className="inline-flex items-center gap-1 rounded-md border border-red-600/40 px-2 py-1 text-[11px] text-red-300 hover:bg-red-500/10"><Ban size={12} /> Kitiltás</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <BanDialog ip={banIp} onClose={() => setBanIp(null)} onDone={() => { setBanIp(null); load(); }} />
    </div>
  );
}
